import React, { useContext, useEffect, useRef, useState } from "react";
import { FormHelperText, Grid, Icon } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { UserContext } from "App";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AddNewsAPI } from "APIs/NewsAPIs";
import FullScreenLoader from "components/Custom/FullScreenLoader";
import { notify } from "App";
// Receipt page theme (shared)
import SectionCard, {
  SectionHeader,
  ranuButtonSx,
  ranuOutlinedButtonSx,
} from "layouts/receipt/components/SectionCard";
import FormField from "layouts/receipt/components/FormField";

// Smaller inner padding for the card, so the content gets more width.
// (only affects the cards inside this wrapper, the shared SectionCard is not changed)
const compactCardsSx = { "& > div": { padding: { xs: "12px", md: "16px" } } };

// Back button: maroon (theme primary) outline and text, arrow icon on the left
const backButtonSx = ({ palette }) => ({
  color: palette.primary.main,
  borderColor: palette.primary.main,
  backgroundColor: "transparent",
  borderRadius: "10px",
  boxShadow: "none",
  boxSizing: "border-box",
  height: { xs: "2.25rem", md: "2.5rem" },
  minHeight: { xs: "2.25rem", md: "2.5rem" },
  px: 2.5,
  fontSize: "0.8rem",
  fontWeight: 700,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  gap: 0.75,
  transition: "background-color .3s ease, color .3s ease, border-color .3s ease",
  // Hover: gold (theme secondary), same as the other outlined buttons
  "&:hover": {
    color: palette.secondary.main,
    borderColor: palette.secondary.main,
    backgroundColor: "transparent",
    boxShadow: "none",
  },
  "&:focus:not(:hover)": { boxShadow: "none" },
});

// The Save button lives outside the <form>, so it points to the form by this id
const FORM_ID = "add-news-form";

// Required fields (checked on blur and when Save is clicked)
const FIELDS = [
  { name: "news_title", label: "Title" },
  { name: "news_description", label: "Description" },
];

const getFieldError = ({ name, label }, data) =>
  String(data?.[name] ?? "").trim() === "" ? `${label} is required` : "";

const AddNews = () => {
  const navigate = useNavigate();
  const userData = useContext(UserContext);
  const dispatch = useDispatch();

  const [newsData, setNewsData] = useState({
    news_title: "",
    news_description: "",
  });

  const [loading, setLoading] = useState(false);

  const [touched, setTouched] = useState({});
  const formRef = useRef(null);
  const [scrollToErrorKey, setScrollToErrorKey] = useState(0);

  // After the errors have been rendered, scroll to the first one
  useEffect(() => {
    if (!scrollToErrorKey) return;
    formRef.current
      ?.querySelector(".Mui-error")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [scrollToErrorKey]);

  const handleBlur = (name) => setTouched((prev) => ({ ...prev, [name]: true }));

  // An error only appears after the field is touched (blurred, or Save was clicked)
  const getVisibleError = (field) => (touched[field.name] ? getFieldError(field, newsData) : "");

  const isFormValid = FIELDS.every((field) => !getFieldError(field, newsData));

  const handleNewsDataChange = (e) => {
    setNewsData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    // Invalid form: show every error and scroll to the first one
    if (!isFormValid) {
      setTouched(Object.fromEntries(FIELDS.map((field) => [field.name, true])));
      setScrollToErrorKey((key) => key + 1);
      return;
    }

    setLoading(true);
    try {
      await AddNewsAPI(newsData, userData.access_token, dispatch);
      notify("News added!", "success");
      navigate("/news");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MDBox px={{ xs: 0, md: 1 }} pt={{ xs: 0, md: 1 }} pb={3}>
      <FullScreenLoader open={loading} />

      {/* Back button sits right beside the page badge (startAction = left of the badge) */}
      <SectionHeader
        icon="post_add"
        label="ADD NEWS"
        description="Fill in the news title and description."
        startAction={
          <MDButton
            type="button"
            variant="outlined"
            color="primary"
            sx={backButtonSx}
            onClick={() => navigate("/news")}
          >
            <Icon sx={{ fontSize: "1.1rem !important", fontWeight: "bold" }}>arrow_back</Icon>
            Back
          </MDButton>
        }
      />

      <form ref={formRef} id={FORM_ID} onSubmit={handleSubmit} noValidate autoComplete="off">
        <MDBox sx={compactCardsSx}>
          <SectionCard title="News" subtitle="Fields marked * are required">
            <Grid container spacing={2}>
              {/* Title Input */}
              <FormField name="title" required full>
                <MDInput
                  name="news_title"
                  type="text"
                  placeholder="Enter title"
                  fullWidth
                  value={newsData.news_title}
                  onChange={handleNewsDataChange}
                  onBlur={() => handleBlur("news_title")}
                  error={!!getVisibleError(FIELDS[0])}
                />
                {getVisibleError(FIELDS[0]) && (
                  <FormHelperText error>{getVisibleError(FIELDS[0])}</FormHelperText>
                )}
              </FormField>

              {/* Description Input */}
              <FormField name="description" required full>
                <MDInput
                  name="news_description"
                  multiline
                  rows={6}
                  placeholder="Enter description"
                  fullWidth
                  value={newsData.news_description}
                  onChange={handleNewsDataChange}
                  onBlur={() => handleBlur("news_description")}
                  error={!!getVisibleError(FIELDS[1])}
                />
                {getVisibleError(FIELDS[1]) && (
                  <FormHelperText error>{getVisibleError(FIELDS[1])}</FormHelperText>
                )}
              </FormField>
            </Grid>
          </SectionCard>
        </MDBox>
      </form>

      {/* Cancel + Save sit outside the form: bottom right on desktop, centered on mobile.
          Save still submits the form through the `form` attribute. */}
      <MDBox display="flex" gap={1.5} justifyContent={{ xs: "center", md: "flex-end" }}>
        <MDButton
          type="button"
          variant="outlined"
          color="primary"
          sx={ranuOutlinedButtonSx}
          disabled={loading}
          onClick={() => navigate("/news")}
        >
          Cancel
        </MDButton>
        <MDButton color="primary" type="submit" form={FORM_ID} sx={ranuButtonSx} disabled={loading}>
          Save News
        </MDButton>
      </MDBox>
    </MDBox>
  );
};

export default AddNews;
